import { useQuery } from "@apollo/client";
import ConfigForm, { ConfigInput } from "components/Layout/ConfigForm";
import { GET_SHIFT_LOCATIONS } from "lib/shifts";
import React, { Fragment, useState, useEffect } from "react";
import PlaybackMap from "./PlaybackMap";
import ShiftPlaybackSlider from "./ShiftPlaybackSlider";
import {
  GetShiftLocationsQuery,
  GetShiftLocationsQueryResult,
} from "generated/graphql";
import { MarkerObject } from "components/Maps/MarkerListMap";
import { getGoogleMaps } from "config/GoogleMaps";
import { LinearProgress } from "@material-ui/core";

interface PlaybackLocationsLoaderProps {
  shiftId: number;
}

export interface LocationPosition {
  lat: number;
  lng: number;
}
interface PlaybackLocation {
  position: LocationPosition;
  timestamp: number;
}

interface ShiftGuardPlaybackObject {
  id: string;
  name: string;
  locations: PlaybackLocation[];
  firstLocationTime?: number;
  lastLocationTime?: number;
  nextIndex: number;
  nextIndexTimeStamp?: number;
}

interface PlaybackLocationsLoaderState {
  markerObjects: MarkerObject[];
  firstLocationTime: number;
  lastLocationTime: number;
  shiftGuards: ShiftGuardPlaybackObject[];
}

const DEFAULT_STATE: PlaybackLocationsLoaderState = {
  markerObjects: [],
  firstLocationTime: 0,
  lastLocationTime: 100,
  shiftGuards: [],
};
export default function PlaybackLocationsLoader({
  shiftId,
}: PlaybackLocationsLoaderProps) {
  const [state, setState] =
    useState<PlaybackLocationsLoaderState>(DEFAULT_STATE);
  const { loading, error, data } = useQuery<GetShiftLocationsQuery>(
    GET_SHIFT_LOCATIONS,
    {
      variables: {
        shiftId: shiftId,
      },
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (!!data) {
      findFirstTime();
    }
  }, [data]);

  const findFirstIndexes = async (time: number): Promise<boolean> => {
    let newShiftGuards: ShiftGuardPlaybackObject[] = [];
    if (time === state.firstLocationTime) {
      state.shiftGuards.forEach((shiftGuard) => {
        if (shiftGuard.firstLocationTime) {
          const newShiftGuard = {
            ...shiftGuard,
            nextIndex: 0,
            nextIndexTimeStamp: shiftGuard.firstLocationTime + 5000,
          };
          newShiftGuards.push(newShiftGuard);
        }
      });
    } else {
      const rangeValue = time;
      state.shiftGuards.forEach((shiftGuard) => {
        const locations = shiftGuard.locations;
        let newNextIndex = 0;
        let newNextIndexTimeStamp = shiftGuard.firstLocationTime;
        for (let i = 0; i < shiftGuard.locations.length; i++) {
          const location = locations[i];
          if (location.timestamp >= rangeValue) {
            newNextIndex = i;
            newNextIndexTimeStamp = location.timestamp;
            break;
          }
        }

        const newShiftGuard = {
          ...shiftGuard,
          nextIndex: newNextIndex,
          nextIndexTimeStamp: newNextIndexTimeStamp,
        };
        newShiftGuards.push(newShiftGuard);
      });
    }

    await setState({ ...state, shiftGuards: newShiftGuards });
    return true;
  };

  const onNewTime = (time: number) => {
    const statingPoint = time;
    const nextRangeValue = statingPoint + 5000;
    let shiftGuards = state.shiftGuards;
    let newShiftGuards: ShiftGuardPlaybackObject[] = [];
    shiftGuards.forEach((shiftGuard) => {
      let newNextIndex = shiftGuard.nextIndex;
      let newNextIndexTimeStamp = shiftGuard.nextIndexTimeStamp;
      if (
        shiftGuard?.nextIndexTimeStamp &&
        shiftGuard?.nextIndexTimeStamp <= statingPoint
      ) {
        let marker = state.markerObjects.find((markerObject) => {
          return markerObject.id === shiftGuard.id;
        });
        const location = shiftGuard.locations[shiftGuard.nextIndex];
        marker?.setPosition(location.position);
        console.log(marker);
        //add logic to check when the next index and timestamp is
        const locations = shiftGuard.locations;
        for (
          let i = shiftGuard.nextIndex;
          i < shiftGuard.locations.length;
          i++
        ) {
          const location = locations[i];
          if (location.timestamp >= nextRangeValue) {
            newNextIndex = i;
            newNextIndexTimeStamp = location.timestamp;
            break;
          }
        }
      }
      const newShiftGuard = {
        ...shiftGuard,
        nextIndex: newNextIndex,
        nextIndexTimeStamp: newNextIndexTimeStamp,
      };
      newShiftGuards.push(newShiftGuard);
    });

    setState({ ...state, shiftGuards: newShiftGuards });
  };

  const findFirstTime = () => {
    let firstTimeStamp = 1000000000000000000;
    let lastTimeStamp = 0;
    let shiftGuards: ShiftGuardPlaybackObject[] = [];

    data?.Shift_Gaurd.forEach((shiftGuard) => {
      let newLocations: any[] = [];
      let firstLocationTime;
      let lastLocationTime;
      shiftGuard.IoLocLocations.forEach((location, index) => {
        const time = new Date(location.DateTime);
        const timestamp = time.getTime();

        firstLocationTime = timestamp;
        if (firstTimeStamp > firstLocationTime) {
          firstTimeStamp = firstLocationTime;
        } else if (index === shiftGuard.IoLocLocations.length - 1) {
          lastLocationTime = timestamp;
          if (lastTimeStamp < lastLocationTime) {
            lastTimeStamp = lastLocationTime;
          }
        }
        const newLocation = {
          ...location,
          position: {
            lat: location.Latitude,
            lng: location.Longitude,
          },
          timestamp: timestamp,
        };
        newLocations.push(newLocation);
      });
      const newShiftGuard = {
        id: shiftGuard.Id,
        name: shiftGuard.Gaurd.GaurdFirstName,
        locations: newLocations,
        firstLocationTime: firstLocationTime,
        lastLocationTime: lastLocationTime,
        nextIndex: 0,
        nextIndexTimeStamp: firstLocationTime,
      };
      shiftGuards.push(newShiftGuard);
      let googleMapPromise = getGoogleMaps();
      if (!window.google) {
        Promise.all([googleMapPromise]).then((values) => {
          let google = values[0];

          addMarker(shiftGuard.Id, shiftGuard.Gaurd.GaurdFirstName);
        });
      } else {
        addMarker(shiftGuard.Id, shiftGuard.Gaurd.GaurdFirstName);
      }
    });
    setState({
      ...state,
      firstLocationTime: firstTimeStamp,
      lastLocationTime: lastTimeStamp,
      shiftGuards: shiftGuards,
    });
  };

  const addMarker = (id: string, name: string) => {
    let containsMarker = false;
    state.markerObjects.forEach((markerObject) => {
      if (markerObject.id === id) {
        containsMarker = true;
      }
    });
    if (!containsMarker) {
      const marker: MarkerObject = new google.maps.Marker({
        position: { lat: 0.0, lng: 0.0 },
        title: name,
        zIndex: 3,
        icon: "https://www.google.com/mapfiles/marker.png",
        label: {
          color: "black",
          fontWeight: "bold",
          text: name,
        },
      });
      marker.id = id;
      const newMarkerObjects = state.markerObjects;
      newMarkerObjects.push(marker);

      setState({ ...state, markerObjects: newMarkerObjects });
    }
  };
  if (loading) {
    return <LinearProgress />;
  } else if (error) {
    return <div>{error.message}</div>;
  } else {
    const shiftPlaybackSlider = (
      <ShiftPlaybackSlider
        findFirstIndexes={findFirstIndexes}
        onNewTime={onNewTime}
        firstLocationTime={state.firstLocationTime}
        lastLocationTime={state.lastLocationTime}
      />
    );
    const inputs: ConfigInput[] = [
      {
        title: "Play Shift",
        type: "custom",
        onChange: () => {},
        xs: 12,
        sm: 12,
        id: "shiftPlaybackController",
        customInput: shiftPlaybackSlider,
      },
    ];
    return (
      <Fragment>
        <ConfigForm inputs={inputs} />
        <PlaybackMap markers={state.markerObjects} />
      </Fragment>
    );
  }
}
